import useUserEditVersion from '@/features/config/hooks/useUserEditVersion'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type MutableRefObject,
} from 'react'
import { usePermission } from '@/hooks/permissions'
import useTranslation from '@/hooks/useTranslation'
import emitterDataConfigApi from '@/api/config/emitterDataConfig'
import { CONFIG_NAME_MAX_LENGTH } from '@/constant/common'
import toast from 'react-hot-toast'
import { getApiErrorMessage, isRetryableApiError } from '@/helpers/notify'
import {
  mergeKnownRows,
  retireCreatedDrafts,
  retireSavedRecords,
  splitEditsForSave,
  withoutKey,
} from '@/utils/stagedSave'
import { Box } from '@mui/material'
import { useSelector } from 'react-redux'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import DeleteIconButton from '@/features/config/components/common/DeleteIconButton'
import DataGrid, {
  gridCellClasses,
  gridRowClasses,
} from '@/components/grid/DataGrid'
import useGuidedAutosave from '@/features/config/hooks/useGuidedAutosave'
import useSerialisedSave from '@/features/config/hooks/useSerialisedSave'
import { useIsGuidedSetup } from '@/features/config/contexts/guided-edit-context'
import type { GridApi, GridCellCtx, GridColumn } from '@/components/grid/types'
import {
  commitOrHoldOpenEditor,
  type GuidedSaveOptions,
} from '@/components/grid/commitOpenEditor'

// Writes must be serialised: the API returns 500 under concurrent writes.
const SAVE_RETRY_LIMIT = 3
const SAVE_RETRY_DELAY_MS = 300
const SAVE_REQUEST_GAP_MS = 120

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/** The permanent "+ Add Emitter" affordance. It holds no data and is never written. */
const ADD_ROW_ID = '__CREATE_ROW__'

/**
 * Draft ids come from a counter seeded here and decremented per draft, so any
 * number of drafts can be staged at once without colliding. Negative throughout:
 * the rest of the grids already read a negative id as "not on the server yet".
 */
const DRAFT_ID_START = -1_000_000
// Row ids arrive from the API as numbers even where the row type says
// string, so coerce before testing the draft marker.
const isDraftId = (id: string | number) => String(id).startsWith('-')

/** Efficiency is a percentage the sizing maths divides by; keep it in range. */
const EFFICIENCY_MIN = 0
const EFFICIENCY_MAX = 300

export interface EmitterRow {
  id: string
  interfaceId: number
  emitter: string
  efficiency?: number
}

// The update endpoint replaces the row, so the pending edit carries the
// emitter's efficiency as well as the new name.
interface PendingEdit {
  emitter: string
  efficiency?: number
}

/** A create staged in the grid; nothing is POSTed until the toolbar Save runs. */
interface DraftRow {
  id: string
  emitter: string
  efficiency?: number
}

export interface EmitterGridActions {
  save: () => Promise<{ failed: number; total: number }>
  cancel: () => void
  refresh: () => void
  hasPendingEdits: () => boolean
  /** Drops a row's staged edit once the row has been deleted. */
  forgetRow: (id: string) => void
  /** Stages a row from outside the grid — the quick-add chips. */
  addDraft: (values?: { emitter?: string; efficiency?: number }) => void
}

interface EmitterTableProps {
  interfaceId: number
  keyword?: string
  bulkEditMode?: boolean
  onDeleteItem?: (id: string, interfaceId: string, name: string) => void
  onRowCountChange?: (count: number) => void
  /** The names staged but not yet written, so a chip can show as added. */
  onStagedNamesChange?: (names: string[]) => void
  actionsRef: MutableRefObject<EmitterGridActions | null>
}

const EmitterTable: FC<EmitterTableProps> = ({
  interfaceId,
  keyword = '',
  bulkEditMode = false,
  onDeleteItem,
  onRowCountChange,
  onStagedNamesChange,
  actionsRef,
}) => {
  const { isViewOnly } = usePermission()
  // SCRUM-1032: inside the wizard the grid writes itself once the user pauses,
  // the same as the decoder and pipe lists. Not on /config, where Save is the
  // contract.
  const isGuidedSetup = useIsGuidedSetup()
  const { t } = useTranslation()
  const isDark = useSelector(selectTheme) === mode_type.dark
  const gridApiRef = useRef<GridApi<EmitterRow> | null>(null)

  // Pending edits live here, NOT in the grid. A refetch replaces the base rows
  // at any time, which would throw away unsaved changes held on the rows.
  const [pendingEdits, setPendingEdits] = useState<Map<string, PendingEdit>>(
    new Map(),
  )
  const [userEditVersion, noteUserEdit] = useUserEditVersion()
  // The ref is the source of truth and is written the moment an edit is
  // staged, not on render: a save that starts with an editor open reads it
  // straight after `commitOpenEditor`, before React has rendered the state the
  // editor's commit set — reading a render-assigned ref missed that edit.
  const pendingEditsRef = useRef(pendingEdits)
  const updatePendingEdits = useCallback(
    (update: (prev: Map<string, PendingEdit>) => Map<string, PendingEdit>) => {
      const next = update(pendingEditsRef.current)
      if (next === pendingEditsRef.current) return
      pendingEditsRef.current = next
      setPendingEdits(next)
    },
    [],
  )

  // Staged creates, in the order they were added. They live outside `rows` so a
  // refetch — which replaces the server rows — cannot drop them; `rowData`
  // splices them back in for display.
  const [drafts, setDrafts] = useState<DraftRow[]>([])
  const draftsRef = useRef(drafts)
  draftsRef.current = drafts
  const draftIdRef = useRef(DRAFT_ID_START)

  // Drafts a save refused to write, so the offending cell can be marked.
  const [invalidRowIds, setInvalidRowIds] = useState<Set<string>>(new Set())

  // All emitters are loaded at once (a small config list) so the "Add Emitter"
  // row can be appended as the real last row rather than pinned to the viewport
  // bottom.
  const [rows, setRows] = useState<EmitterRow[]>([])
  // Every row fetched, kept across a search so an edit on a row the search
  // hides is still sent (see splitEditsForSave). `listCompleteRef` says the
  // last fetch had no search, so a row missing from it has been deleted.
  const knownRowsRef = useRef<Map<string, EmitterRow>>(new Map())
  const listCompleteRef = useRef(false)
  // Only the FIRST fetch draws skeleton rows. A refetch after a save or a
  // keyword change keeps the rows that are already on screen, so the list
  // never blinks out from under the user.
  const [loadedOnce, setLoadedOnce] = useState(false)

  const fetchRows = useCallback(async () => {
    try {
      const res: any = await emitterDataConfigApi.getAll({
        interfaceId,
        PageIndex: 1,
        PageSize: 0,
        ...(keyword ? { Keyword: keyword } : undefined),
      } as any)
      const list: EmitterRow[] = res?.data?.data ?? []
      knownRowsRef.current = mergeKnownRows(knownRowsRef.current, list, !keyword)
      listCompleteRef.current = !keyword
      setRows(list)
      onRowCountChange?.(res?.data?.totalCount ?? list.length)
    } catch (error: any) {
      toast.error(error?.message || t('somethingWentWrong'))
    } finally {
      setLoadedOnce(true)
    }
  }, [interfaceId, keyword, onRowCountChange, t])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  const refresh = useCallback(() => {
    fetchRows()
  }, [fetchRows])

  // The chip stays put once a draft has been added, so several emitters can be
  // staged one after another.
  const addRow = useMemo<EmitterRow | null>(
    () =>
      bulkEditMode && !isViewOnly
        ? { id: ADD_ROW_ID, interfaceId, emitter: '' }
        : null,
    [bulkEditMode, isViewOnly, interfaceId],
  )

  // Leaving bulk edit ends the staged batch, whichever way it ended.
  useEffect(() => {
    if (bulkEditMode) return
    draftsRef.current = []
    setDrafts([])
    setInvalidRowIds(new Set())
    draftIdRef.current = DRAFT_ID_START
  }, [bulkEditMode])

  const rowData = useMemo(() => {
    const merged = rows.map((row) => {
      const edit = pendingEdits.get(String(row.id))
      return edit
        ? { ...row, emitter: edit.emitter, efficiency: edit.efficiency }
        : row
    })
    const staged = drafts.map<EmitterRow>((draft) => ({
      id: draft.id,
      interfaceId,
      emitter: draft.emitter,
      efficiency: draft.efficiency,
    }))
    const all = [...merged, ...staged]
    return addRow ? [...all, addRow] : all
  }, [rows, pendingEdits, drafts, addRow, interfaceId])

  const getRowId = useCallback((row: EmitterRow) => String(row.id), [])

  // `rowData` already has the staged edits merged in, so the amber marker
  // compares against the untouched server rows — otherwise editing one field
  // would light up every cell in the row.
  const baseRowById = useMemo(() => {
    const map = new Map<string, EmitterRow>()
    rows.forEach((row) => map.set(String(row.id), row))
    return map
  }, [rows])

  const isFieldChanged = useCallback(
    (rowId: string, field: 'emitter' | 'efficiency') => {
      const edit = pendingEdits.get(rowId)
      if (!edit) return false
      const base = baseRowById.get(rowId)
      if (!base) return false
      return edit[field] !== base[field]
    },
    [pendingEdits, baseRowById],
  )

  // Keep the staged drafts and the "+ Add Emitter" chip below the saved rows
  // regardless of sort, in the order they were added.
  const postSort = useCallback((list: EmitterRow[]) => {
    const chip = list.find((row) => row.id === ADD_ROW_ID)
    const rest = list.filter((row) => row.id !== ADD_ROW_ID)
    const staged = rest
      .filter((row) => isDraftId(row.id))
      .sort((a, b) => Number(b.id) - Number(a.id))
    const merged = [...rest.filter((row) => !isDraftId(row.id)), ...staged]
    return chip ? [...merged, chip] : merged
  }, [])

  // --- Staged drafts --------------------------------------------------------

  // The row is only in the DOM after the drafts state has rendered, so the
  // editor waits a tick.
  const startEditingRow = useCallback((rowId: string) => {
    setTimeout(() => {
      gridApiRef.current?.scrollToRow(rowId)
      gridApiRef.current?.startEditingCell(rowId, 'emitter')
    }, 0)
  }, [])

  /**
   * `values` arrive from the quick-add chips, which stage a named row rather
   * than writing one — the chip is a shortcut for typing the name, not a
   * decision to create the record. A chip-staged row is not opened for editing:
   * it already says what the user asked for, and stealing focus would fight the
   * next chip click.
   */
  const handleAddDraft = useCallback(
    (values?: { emitter?: string; efficiency?: number }) => {
      if (isViewOnly) return
      gridApiRef.current?.stopEditing()
      const id = String(draftIdRef.current)
      draftIdRef.current -= 1
      const next = [
        ...draftsRef.current,
        {
          id,
          emitter: values?.emitter ?? '',
          ...(values?.efficiency === undefined
            ? undefined
            : { efficiency: values.efficiency }),
        },
      ]
      draftsRef.current = next
      setDrafts(next)
      noteUserEdit()
      if (!values?.emitter) startEditingRow(id)
      else gridApiRef.current?.scrollToRow(id)
    },
    [isViewOnly, startEditingRow, noteUserEdit],
  )

  /** Drops a single staged draft, leaving the rest of the batch alone. */
  const handleDiscardDraft = useCallback((id: string) => {
    const next = draftsRef.current.filter((draft) => draft.id !== id)
    draftsRef.current = next
    setDrafts(next)
    setInvalidRowIds((prev) => {
      if (!prev.has(id)) return prev
      const remaining = new Set(prev)
      remaining.delete(id)
      return remaining
    })
  }, [])

  const handleCellValueChanged = useCallback(
    (event: {
      row: EmitterRow
      field: string
      oldValue: unknown
      newValue: unknown
    }) => {
      noteUserEdit()
      if (event.field !== 'emitter' && event.field !== 'efficiency') return

      // The editor already clamps to EFFICIENCY_MIN..EFFICIENCY_MAX, so a value
      // arriving here is in range; blank clears it back to "not set".
      const isEfficiency = event.field === 'efficiency'
      const typedEfficiency =
        event.newValue === undefined || event.newValue === null
          ? undefined
          : Number(event.newValue)

      // Typing into a staged draft stages the value — it is not written until
      // the toolbar Save runs, exactly like an edit to an existing row. The ref
      // is written straight away as well: Save blurs the open editor, which
      // commits through here in the same tick, where state would still be stale.
      if (isDraftId(event.row.id)) {
        const typed = String(event.newValue ?? '')
        const next = draftsRef.current.map((draft) =>
          draft.id === event.row.id
            ? {
                ...draft,
                ...(isEfficiency
                  ? { efficiency: typedEfficiency }
                  : { emitter: typed }),
              }
            : draft,
        )
        draftsRef.current = next
        setDrafts(next)
        if (!isEfficiency && typed.trim()) {
          setInvalidRowIds((prev) => {
            if (!prev.has(event.row.id)) return prev
            const remaining = new Set(prev)
            remaining.delete(event.row.id)
            return remaining
          })
        }
        return false as const
      }

      // The chip row carries the "+ Add …" affordance and holds no value.
      if (event.row.id === ADD_ROW_ID) return false as const

      const row = event.row
      // An edit replaces the whole record, so the staged edit always carries
      // both fields — whichever one was not touched keeps the row's value.
      const staged = pendingEditsRef.current.get(String(row.id))
      const currentName = staged?.emitter ?? row.emitter
      const currentEfficiency = staged ? staged.efficiency : row.efficiency

      if (isEfficiency) {
        if (typedEfficiency === currentEfficiency) return false as const
        updatePendingEdits((prev) => {
          const map = new Map(prev)
          map.set(String(row.id), {
            emitter: currentName,
            efficiency: typedEfficiency,
          })
          return map
        })
        return false as const
      }

      const next = String(event.newValue ?? '').trim()
      // Clearing the name is not a delete, and an over-long one is rejected —
      // both put the old value back.
      if (!next) return false as const
      if (next.length > CONFIG_NAME_MAX_LENGTH) {
        toast.error(
          t(`Name must not exceed ${CONFIG_NAME_MAX_LENGTH} characters`),
        )
        return false as const
      }
      if (next === currentName) return false as const

      updatePendingEdits((prev) => {
        const map = new Map(prev)
        map.set(String(row.id), {
          emitter: next,
          efficiency: currentEfficiency,
        })
        return map
      })
      return false as const
    },
    [t, noteUserEdit, updatePendingEdits],
  )

  // Enter needs no handling of its own: the text editor commits on Enter, which
  // stages the name through `handleCellValueChanged` like any other edit.

  // --- Bulk save / cancel ---------------------------------------------------

  const performSave = useCallback(async (options?: GuidedSaveOptions) => {
    // Commits the open editor before the staged rows are read. Not a
    // `sleep(0)`: a macrotask is throttled to one callback a minute in a
    // hidden tab, which parked the save (SCRUM-1032 QC, 6 Oct). From the
    // autosave with an editor still open, that row is skipped instead and
    // its editor left alone (SCRUM-1032 QC, 30 Oct).
    const heldRowId = commitOrHoldOpenEditor(gridApiRef.current, options)

    const stagedDrafts = draftsRef.current
      .filter((draft) => String(draft.id) !== heldRowId)
      .map((draft) => ({
      ...draft,
      emitter: draft.emitter.trim(),
    }))

    // Every staged row is validated before anything is written: a half-applied
    // save would leave the user guessing which rows landed.
    const unnamed = stagedDrafts.filter((draft) => !draft.emitter)
    const tooLong = stagedDrafts.filter(
      (draft) => draft.emitter.length > CONFIG_NAME_MAX_LENGTH,
    )
    const invalid = [...unnamed, ...tooLong]
    if (invalid.length > 0) {
      setInvalidRowIds(new Set(invalid.map((draft) => draft.id)))
      toast.error(
        unnamed.length > 0
          ? t('Name is required')
          : t(`Name must not exceed ${CONFIG_NAME_MAX_LENGTH} characters`),
      )
      // A non-zero `failed` keeps the page in bulk edit, so the batch survives.
      return { failed: invalid.length, total: stagedDrafts.length }
    }
    setInvalidRowIds(new Set())

    // Every edit on a row the grid has fetched is sent, including one a search
    // now hides — skipping those reported a clean save while the value never
    // reached the server. An edit on a row an unfiltered list no longer has
    // (deleted, here or elsewhere) cannot land: it is dropped rather than
    // 404ing on every pass and holding the step for good.
    const { send: edits, gone } = splitEditsForSave(
      heldRowId
        ? withoutKey(pendingEditsRef.current, heldRowId)
        : pendingEditsRef.current,
      new Set(knownRowsRef.current.keys()),
      listCompleteRef.current,
    )
    if (gone.length > 0)
      updatePendingEdits((prev) =>
        gone.reduce((map, id) => withoutKey(map, id), prev),
      )

    // Thunks, not promises: firing every request up front sent the whole batch
    // in parallel, and this API returns 500 under concurrent writes.
    const updateTasks = edits.map(
      ([id, edit]) =>
        () =>
          emitterDataConfigApi.update({
            id: Number(id),
            interfaceId: Number(interfaceId),
            emitter: edit.emitter,
            ...(edit.efficiency == null ? {} : { efficiency: edit.efficiency }),
          }),
    )
    const createTasks = stagedDrafts.map(
      (draft) => () =>
        emitterDataConfigApi.create({
          interfaceId,
          emitter: draft.emitter,
          ...(draft.efficiency == null ? {} : { efficiency: draft.efficiency }),
        }),
    )

    const tasks = [...updateTasks, ...createTasks]
    if (tasks.length === 0) return { failed: 0, total: 0 }

    // One at a time, with a retry: the API returns 500 when writes arrive
    // concurrently, which silently dropped most of a large batch.
    let lastError: any = null
    const failedTaskIndexes = new Set<number>()

    for (const [index, task] of tasks.entries()) {
      let saved = false

      for (let attempt = 0; attempt < SAVE_RETRY_LIMIT && !saved; attempt++) {
        try {
          await task()
          saved = true
        } catch (error: any) {
          lastError = error
          // A 4xx is the server refusing the value: resending cannot help.
          if (!isRetryableApiError(error)) break
          if (attempt < SAVE_RETRY_LIMIT - 1) await sleep(SAVE_RETRY_DELAY_MS)
        }
      }

      if (!saved) failedTaskIndexes.add(index)
      await sleep(SAVE_REQUEST_GAP_MS)
    }

    // Retire what landed from the CURRENT staged state, not from the copy
    // taken when the save began: a chip clicked or a name typed while the
    // requests were out is newer than that copy and must survive. Failed
    // edits and drafts stay staged as they are (SCRUM-1032 review, round 7).
    const landedEdits = edits.filter((_, index) => !failedTaskIndexes.has(index))
    updatePendingEdits((prev) => retireSavedRecords(prev, landedEdits))

    // The creates trail the updates in `tasks`, so draft N is task N + offset.
    // Re-staging a draft that landed would create it a second time.
    const createOffset = updateTasks.length
    const landedDraftIds = stagedDrafts
      .filter((_, index) => !failedTaskIndexes.has(index + createOffset))
      .map((draft) => draft.id)
    draftsRef.current = retireCreatedDrafts(draftsRef.current, landedDraftIds)
    setDrafts(draftsRef.current)

    const failed = failedTaskIndexes.size

    if (failed === 0) {
      // SCRUM-1032: inside the wizard the step shows one status for every
      // grid on it, so six chips added one at a time no longer raise six
      // toasts. On /config, Save is a deliberate act and still confirms.
      if (!isGuidedSetup)
        toast.success(
          createTasks.length > 0 && updateTasks.length === 0
            ? t('addEmitterSuccess')
            : t('updateEmitterSuccess'),
        )
    } else if (failed === tasks.length) {
      // The server's reason, under one id so a repeat replaces it.
      toast.error(getApiErrorMessage(lastError, t('somethingWentWrong')), {
        id: 'emitter-save-error',
      })
    } else {
      toast.error(t('bulkSavePartialError', { failed, total: tasks.length }), {
        id: 'emitter-save-error',
      })
    }

    if (failed < tasks.length) refresh()

    return { failed, total: tasks.length }
  }, [interfaceId, t, refresh, updatePendingEdits, isGuidedSetup])

  // SCRUM-1032 QC items 12 / 15b — one save at a time. The pause autosave
  // and the wizard's Next flush both call this, and two overlapping runs
  // read the same staged rows and write them twice.
  const { save: handleSave, isSaving } = useSerialisedSave(performSave)

  /** A deleted row takes its staged edit with it; nothing is left "held". */
  const forgetRow = useCallback(
    (id: string) => {
      knownRowsRef.current.delete(String(id))
      updatePendingEdits((prev) => withoutKey(prev, String(id)))
    },
    [updatePendingEdits],
  )

  const handleCancel = useCallback(() => {
    updatePendingEdits(() => new Map())
    draftsRef.current = []
    setDrafts([])
    setInvalidRowIds(new Set())
    draftIdRef.current = DRAFT_ID_START
    refresh()
  }, [refresh, updatePendingEdits])

  // Expose the toolbar actions to the parent without global window handles.
  useEffect(() => {
    actionsRef.current = {
      save: handleSave,
      cancel: handleCancel,
      refresh,
      hasPendingEdits: () =>
        pendingEditsRef.current.size > 0 || draftsRef.current.length > 0,
      forgetRow,
      addDraft: handleAddDraft,
    }
    return () => {
      actionsRef.current = null
    }
  }, [actionsRef, handleSave, handleCancel, refresh, forgetRow, handleAddDraft])

  // A row still missing its name cannot be written — `handleSave` rejects it
  // and marks the cell — so autosave waits rather than raising the same
  // complaint every few seconds while the user is still typing it. A chip
  // stages a named row, so only a hand-added blank one holds the batch.
  const draftsReadyToSave = drafts.every((draft) =>
    String(draft.emitter ?? '').trim(),
  )

  useGuidedAutosave({
    enabled: isGuidedSetup && bulkEditMode && !isViewOnly && draftsReadyToSave,
    hasPendingChanges: pendingEdits.size > 0 || drafts.length > 0,
    // User edits only: see useUserEditVersion.
    version: userEditVersion,
    save: handleSave,
    keepsEditorOpen: true,
    // An open editor is the user mid-value; the save would close it.
    isEditing: () => gridApiRef.current?.isEditingCell() ?? false,
    isBusy: isSaving,
  })

  // The chips show a name as added while it is only staged, so one cannot be
  // staged twice before the autosave writes it.
  useEffect(() => {
    onStagedNamesChange?.(drafts.map((draft) => draft.emitter))
  }, [drafts, onStagedNamesChange])

  const ActionsCellRenderer = useCallback(
    ({ row }: GridCellCtx<EmitterRow>) => {
      if (!row) return null

      // The chip row is an affordance, not a record — nothing to act on.
      if (row.id === ADD_ROW_ID) return null
      if (!bulkEditMode) return null

      // A draft gets the same ✕ as a saved row, so one staged item can be
      // dropped without cancelling the whole batch. It has never been written,
      // so it is discarded locally instead of going through the delete modal.
      const isDraft = isDraftId(row.id)

      return (
        <Box data-no-cell-edit>
          <DeleteIconButton
            disabled={isViewOnly}
            isDark={isDark}
            onClick={() => {
              if (isViewOnly) return
              if (isDraft) handleDiscardDraft(row.id)
              else
                onDeleteItem?.(
                  row.id,
                  String(row.interfaceId ?? interfaceId),
                  row.emitter,
                )
            }}
          />
        </Box>
      )
    },
    [
      bulkEditMode,
      isViewOnly,
      isDark,
      onDeleteItem,
      interfaceId,
      handleDiscardDraft,
    ],
  )

  const columns = useMemo<GridColumn<EmitterRow>[]>(
    () => [
      {
        field: 'emitter',
        headerName: t('tableHeaderEmitter'),
        // These lists sit in a narrow card on the Station Setup tab, so the
        // name yields space rather than pushing efficiency out of view. Capped
        // as well: an emitter name is a couple of words, and letting the column
        // take every spare pixel is what pushed efficiency to the far edge of
        // the card with a gulf of empty row between the two.
        flex: 2,
        minWidth: 120,
        maxWidth: 240,
        editable: ({ row }) =>
          row.id === ADD_ROW_ID ? false : bulkEditMode && !isViewOnly,
        editor: 'text',
        editorParams: { maxLength: CONFIG_NAME_MAX_LENGTH },
        cellRenderer: ({ row, value }) => {
          if (row.id === ADD_ROW_ID) {
            return (
              <Box
                component="span"
                data-no-cell-edit
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '2px 10px',
                  height: '40px',
                  borderRadius: '4px',
                  border: isViewOnly
                    ? '1px dashed #e0e0e0'
                    : `1px dashed ${
                        isDark
                          ? 'rgba(52, 211, 153, 0.5)'
                          : 'rgba(0, 71, 68, 0.5)'
                      }`,
                  cursor: isViewOnly ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: isViewOnly
                    ? '#bdbdbd'
                    : isDark
                      ? '#5fe0ad'
                      : '#004744',
                }}
                onClick={() => handleAddDraft()}
              >
                + {t('actionAddEmitter')}
              </Box>
            )
          }
          return <>{value as string}</>
        },
        cellClass: ({ row }) => {
          const list: string[] = []
          // A draft takes the whole-row `new` tint instead of the per-cell
          // editable ring, so the two signals stay distinguishable.
          if (
            bulkEditMode &&
            !isViewOnly &&
            row.id !== ADD_ROW_ID &&
            !isDraftId(row.id)
          ) {
            list.push(gridCellClasses.editable)
          }
          if (isFieldChanged(String(row.id), 'emitter')) {
            list.push(gridCellClasses.changed)
          }
          if (invalidRowIds.has(row.id)) list.push(gridCellClasses.invalid)
          return list
        },
      },
      {
        field: 'efficiency',
        headerName: t('tableHeaderEfficiency'),
        // Shares the row with the name rather than sitting at the far right:
        // two columns pinned to opposite edges left a gulf of empty space
        // between a name and its percentage. Left-aligned for the same reason
        // — the value reads as the second column of a list, not as a figure in
        // a numeric table. It takes whatever the capped name column leaves, so
        // the empty space lands after the last value instead of between the
        // two columns; the minimum covers the "Efficiency" header, which is
        // wider than any value it holds.
        flex: 1,
        minWidth: 104,
        editable: ({ row }) =>
          row.id === ADD_ROW_ID ? false : bulkEditMode && !isViewOnly,
        editor: 'number',
        // The editor clamps to this range, so an out-of-range value cannot be
        // committed in the first place and needs no separate save-time check.
        editorParams: { min: EFFICIENCY_MIN, max: EFFICIENCY_MAX },
        cellRenderer: ({ row, value }) => {
          if (row.id === ADD_ROW_ID) return null
          return <>{value == null || value === '' ? '-' : `${value}%`}</>
        },
        cellClass: ({ row }) => {
          const list: string[] = []
          if (row.id === ADD_ROW_ID) return list
          if (bulkEditMode && !isViewOnly && !isDraftId(row.id)) {
            list.push(gridCellClasses.editable)
          }
          if (isFieldChanged(String(row.id), 'efficiency')) {
            list.push(gridCellClasses.changed)
          }
          return list
        },
      },
      // Delete is the only action and it only applies in bulk edit. Outside
      // it the renderer drew nothing but the column still reserved its width,
      // which was enough to push these narrow panels into a horizontal
      // scrollbar over an empty strip.
      ...(bulkEditMode
        ? [
            {
              field: 'actions',
              headerName: '',
              // One 30px icon button. 84 left a band of empty space beside it
              // and, with the name column's minimum, pushed the row wider than
              // the panel.
              width: 52,
              // Pinned so Delete stays reachable: these panels are narrow and
              // the row scrolls internally, which used to carry the action
              // column out of sight. Matches EtReference.
              pinned: 'right' as const,
              sortable: false,
              resizable: false,
              editable: false,
              cellRenderer: ActionsCellRenderer,
              cellStyle: {
                justifyContent: 'flex-end',
                paddingRight: '12px',
                overflow: 'visible',
              },
            },
          ]
        : []),
    ],
    [
      t,
      ActionsCellRenderer,
      bulkEditMode,
      isViewOnly,
      isDark,
      isFieldChanged,
      invalidRowIds,
      handleAddDraft,
    ],
  )

  const rowClass = useCallback(
    ({ row }: { row: EmitterRow }) =>
      isDraftId(row.id) ? gridRowClasses.new : undefined,
    [],
  )

  return (
    <DataGrid<EmitterRow>
      columns={columns}
      rows={rowData}
      loading={!loadedOnce}
      getRowId={getRowId}
      postSort={postSort}
      apiRef={gridApiRef}
      rowHeight={40}
      headerHeight={42}
      editMode="singleClick"
      rowClass={rowClass}
      onCellValueChanged={handleCellValueChanged}
    />
  )
}

export default EmitterTable
